import axiosInstance from '@/utils/axios';
import { API_BASE_URL } from '@/config/api.config';
import type { Test } from '@/types';

const API_URL = API_BASE_URL;

export interface TestFilters {
  course_id?: string;
  exam_id?: string;
  test_type?: string;
  is_active?: boolean;
  search?: string;
}

class TestsService {
  /**
   * Get all tests with optional filters
   */
  async getTests(filters?: TestFilters): Promise<Test[]> {
    const params = new URLSearchParams();
    if (filters?.course_id) params.append('course_id', filters.course_id);
    if (filters?.exam_id) params.append('exam_id', filters.exam_id);
    if (filters?.test_type) params.append('test_type', filters.test_type);
    if (filters?.is_active !== undefined) params.append('is_active', filters.is_active.toString());
    if (filters?.search) params.append('search', filters.search);

    const response = await axiosInstance.get(`${API_URL}/tests?${params.toString()}`);
    return response.data.data || [];
  }

  /**
   * Get test by ID
   */
  async getTestById(testId: string): Promise<Test | null> {
    const response = await axiosInstance.get(`${API_URL}/tests/${testId}`);
    return response.data.data;
  }

  /**
   * Create new test
   */
  async createTest(testData: Partial<Test>): Promise<Test> {
    const response = await axiosInstance.post(`${API_URL}/tests`, testData);
    return response.data.data;
  }

  /**
   * Update test
   */
  async updateTest(testId: string, testData: Partial<Test>): Promise<Test> {
    const response = await axiosInstance.put(`${API_URL}/tests/${testId}`, testData);
    return response.data.data;
  }

  /**
   * Delete test
   */
  async deleteTest(testId: string): Promise<void> {
    await axiosInstance.delete(`${API_URL}/tests/${testId}`);
  }

  /**
   * Get questions assigned to test
   */
  async getTestQuestions(testId: string): Promise<any[]> {
    const response = await axiosInstance.get(`${API_URL}/tests/${testId}/questions`);
    return response.data.data || [];
  }

  /**
   * Assign questions to test (replaces existing)
   */
  async assignQuestions(testId: string, questionIds: string[]): Promise<void> {
    await axiosInstance.put(`${API_URL}/tests/${testId}/questions`, { question_ids: questionIds });
  }

  /**
   * Add questions to test (keeps existing)
   */
  async addQuestions(testId: string, questionIds: string[]): Promise<void> {
    await axiosInstance.post(`${API_URL}/tests/${testId}/questions`, { question_ids: questionIds });
  }

  /**
   * Remove questions from test
   */
  async removeQuestions(testId: string, questionIds: string[]): Promise<void> {
    await axiosInstance.delete(`${API_URL}/tests/${testId}/questions`, { data: { question_ids: questionIds } });
  }

  /**
   * Get test statistics
   */
  async getTestStats(testId: string): Promise<any> {
    const response = await axiosInstance.get(`${API_URL}/tests/${testId}/stats`);
    return response.data.data;
  }
}

export const testsService = new TestsService();
